import { OwnerTypes } from "@/domain/_shared/lib/owner-types";
import { DeleteAssigneeInput, DeleteAssigneeMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { deleteAssigneeMutationDocument } from "./assignee.gql";
import { EVENT_ASSIGNEE_DELETED, trackAssigneeEvent } from "./tracking";

export type DeletedAssignee = DeleteAssigneeMutation["deleteAssignee"];

export interface UseDeleteAssigneeProps {
  onCompleted?: (deletedAssignee: DeletedAssignee) => void;
  onError?: (e: Error) => void;
}

export const useDeleteAssignee = ({ onCompleted, onError }: UseDeleteAssigneeProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: DeleteAssigneeInput) => {
      const client = createOperationClient("deleteAssignee");
      const data = await client.request(deleteAssigneeMutationDocument, { input });
      return data.deleteAssignee;
    },
    onSuccess: (deletedAssignee) => {
      // Remove assignee from cache
      queryClient.removeQueries({
        queryKey: queryKeys.assignees.detail(deletedAssignee.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignees.byOwner(deletedAssignee.ownerId),
      });

      // Invalidate related entity queries to update UI
      if (deletedAssignee.ownerType === OwnerTypes.CLIMATE_ACTION) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.climateActions.detail(deletedAssignee.ownerId),
        });
        // Also invalidate climate action lists to update datatable section headers
        queryClient.invalidateQueries({
          queryKey: queryKeys.climateActions.lists(),
        });
      }

      if (deletedAssignee.ownerType === OwnerTypes.PROJECT) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.detail(deletedAssignee.ownerId),
        });
        // Also invalidate project lists to update datatable section headers
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.lists(),
        });
      }

      if (deletedAssignee.ownerType === OwnerTypes.TASK) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tasks.detail(deletedAssignee.ownerId),
        });
        // Also invalidate task lists to update datatable section headers
        queryClient.invalidateQueries({
          queryKey: queryKeys.tasks.lists(),
        });
      }

      trackAssigneeEvent(EVENT_ASSIGNEE_DELETED, deletedAssignee);

      if (onCompleted) {
        onCompleted(deletedAssignee);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    deleteAssignee: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
